/** Constants, tab/document-type predicates, merge helpers and the create-dispatch for document upload. */

/** Internal. */
import { reducerList } from 'data/shipmentMainTableReducers';

export const ACCEPT = '.pdf';
export const MAX_FILES = 20;

/** document_type values the parser can return: MBL | MAWB | HBL | HAWB | UNKNOWN. */
const MASTER_DOCUMENT_TYPES = ['MBL', 'MAWB'];
const HOUSE_DOCUMENT_TYPES = ['HBL', 'HAWB'];

const normalizeDocumentType = (documentType) => String(documentType ?? '').toUpperCase();

/** Single source of truth for the MBL/MAWB check (was duplicated in 3 places). */
export const isMblOrMawb = (documentType) => MASTER_DOCUMENT_TYPES.includes(normalizeDocumentType(documentType));

/** Single source of truth for the HBL/HAWB check (was duplicated in 3 places). */
export const isHblOrHawb = (documentType) => HOUSE_DOCUMENT_TYPES.includes(normalizeDocumentType(documentType));

/** Shipment tabs: 0/1 ocean import/export, 2/3 air import/export, 4 trucking. */
const OCEAN_TABS = [0, 1];
const AIR_TABS = [2, 3];

export const isOceanTab = (tabValue) => OCEAN_TABS.includes(tabValue);
export const isAirTab = (tabValue) => AIR_TABS.includes(tabValue);
/** Ocean export is the only tab carrying a container seal number. */
export const isOceanExportTab = (tabValue) => tabValue === 1;

/** Parsed-data key holding the house bill number. Non-ocean tabs use `hawb`. */
export const getHblNumberKey = (tabValue) => (isOceanTab(tabValue) ? 'hbl' : 'hawb');

/** Table names per tab (same order as socketHandler handleSocketCreate). */
const TABLE_NAMES = {
    0: ['il_ocean_import', 'il_ocean_import_hbl', 'il_ocean_import_cntr', 'il_rate'],
    1: ['il_ocean_export', 'il_ocean_export_hbl', 'il_ocean_export_cntr', 'il_rate'],
    2: ['il_air_import', 'il_air_import_hbl', 'il_air_import_hbl_alias', 'il_rate'],
    3: ['il_air_export', 'il_air_export_hbl', 'il_air_export_hbl_alias', 'il_rate'],
    4: ['il_ftl_ltl', 'il_ftl_ltl_alias', 'il_rate'],
};

/** Reducer names per tab, index-aligned with TABLE_NAMES. */
const REDUCER_NAMES = {
    0: ['addOimMainForm', 'addOimHBL', 'addOimContainer', 'addOimRate'],
    1: ['addOexMainForm', 'addOexHBL', 'addOexContainer', 'addOexRate'],
    2: ['addAimMainForm', 'addAimHBL', 'addAimContainer', 'addAimRate'],
    3: ['addAexMainForm', 'addAexHBL', 'addAexContainer', 'addAexRate'],
    4: ['addTruckingMainForm', 'addBOL', 'addTruckingRate'],
};

/** Dispatch created shipment data to Redux (same shape as socket create). */
export function dispatchCreatedShipment(dispatch, tabValue, payload) {
    if (!payload || typeof payload !== 'object') return;
    const tableNames = TABLE_NAMES[tabValue];
    const reducerNames = REDUCER_NAMES[tabValue];
    if (!tableNames || !reducerNames) return;
    tableNames.forEach((key, index) => {
        const data = payload[key];
        const reducer = reducerList[reducerNames[index]];
        if (!reducer || !data) return;
        if (Array.isArray(data)) {
            data.forEach((item) => dispatch(reducer(item)));
        } else {
            dispatch(reducer(data));
        }
    });
}

/* ------------------------------------------------------------------ *
 * Merge helpers shared by "Create shipment" and "Apply to form"
 * (pure: no state, no dispatch)
 * ------------------------------------------------------------------ */

/** Grid row ids are `${fileId}-${index}`, so compare numeric-aware to keep the parsed order. */
function sortRowsById(rows) {
    return [...rows].sort((a, b) => String(a.id).localeCompare(String(b.id), undefined, { numeric: true }));
}

/** Rows of a single file, in parsed order. */
export function getFileRows(rows, fileId) {
    return sortRowsById(rows.filter((row) => row.fileId === fileId));
}

/** Parsed data of a file, preferring the previewEdited copy. */
export function getParsedDataBase(file) {
    return (file?.previewEdited ?? file?.parsedData) || {};
}

/**
 * The MBL/MAWB file of a batch: an explicitly typed master document, otherwise the first file
 * unless every file is a house document.
 */
export function findMblFile(successFiles) {
    return (
        successFiles.find((file) => isMblOrMawb(file.document_type)) ??
        (successFiles.every((file) => isHblOrHawb(file.document_type)) ? null : successFiles[0])
    );
}

/** True when the parser found no containers and the grid only holds the '-' placeholder row. */
function isPlaceholderContainerRows(rows) {
    if (rows.length !== 1) return false;
    const [row] = rows;
    return (row.containerSummary === '-' || row.containerSummary === '') && row.containerVolume === '-';
}

/** Container payload objects from the edited ocean grid rows. */
function buildContainersFromRows(rows, tabValue) {
    const includeSeal = isOceanExportTab(tabValue);
    return rows.map((row) => {
        const container = {
            number: row.containerSummary ?? '',
            cntr_number: row.containerSummary ?? '',
            volume: row.containerVolume ?? '',
            size: row.containerVolume ?? '',
        };
        if (includeSeal) container.seal_number = row.containerSeal ?? '';
        return container;
    });
}

/** Alias payload objects from the edited air/trucking grid rows. */
function buildAliasesFromRows(rows) {
    return rows.map((row) => ({
        alias: row.alias ?? '',
        flight: row.flight ?? '',
        il_opus_vessel_id: row.flight ?? '',
        volume: row.volume != null ? row.volume : 0,
        quantity: row.volume != null ? row.volume : 0,
        unit: row.unit ?? '',
        il_opus_volume_unit_id: row.unit ?? '',
        weight: row.weight != null ? Number(row.weight) : 0,
    }));
}

/** Merge the edited MBL/MAWB grid row back into the parsed master data. */
export function mergeMblRow(base, mblRow, tabValue) {
    if (mblRow == null) return base;
    if (isAirTab(tabValue)) {
        return {
            ...base,
            mawb: mblRow.mawb,
            etd: mblRow.etd,
            eta: mblRow.eta,
            aol: mblRow.aol,
            aod: mblRow.aod,
            faod: mblRow.faod,
        };
    }
    return {
        ...base,
        pol: mblRow.pol,
        pod: mblRow.pod,
        fpod: mblRow.fpod,
        vessel: mblRow.vessel,
        etd: mblRow.etd,
        eta: mblRow.eta,
        mbl: mblRow.mbl,
    };
}

/** Merge one file's edited HBL/HAWB grid rows back into its parsed house data. */
export function mergeHblRows(base, fileRows, tabValue) {
    const rows = sortRowsById(fileRows);
    if (rows.length === 0) return base;
    const [firstRow] = rows;
    const hblNumKey = getHblNumberKey(tabValue);

    if (isOceanTab(tabValue)) {
        const containers = isPlaceholderContainerRows(rows) ? base.containers : buildContainersFromRows(rows, tabValue);
        return {
            ...base,
            [hblNumKey]: firstRow[hblNumKey],
            containers: Array.isArray(containers) && containers.length ? containers : base.containers,
        };
    }

    const aliases = buildAliasesFromRows(rows);
    return {
        ...base,
        [hblNumKey]: firstRow[hblNumKey],
        aliases: aliases.length ? aliases : base.aliases ?? [],
    };
}
